<script lang="ts">
  import { onMount } from 'svelte';
  import { setupProjectAnimations } from '$lib/utils/animations';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import ProjectHeroFullWidth from '$lib/components/ProjectHeroFullWidth.svelte';
  import FullWidthImage from '$lib/components/FullWidthImage.svelte';
  import ChallengeSolution from '$lib/components/ChallengeSolution.svelte';
  import Quote from '$lib/components/Quote.svelte';

  onMount(() => {
    setupProjectAnimations();
  });
</script>

<div class="min-h-screen bg-white">
  <ProjectHeroFullWidth
    title="Berlin Budget Explorer"
    client="City of Berlin (Prototype)"
    industry="Civic Tech"
    product="Interactive Tool for Public Budget Transparency"
    heroMedia={{
      type: 'image',
      src: '/projects/berlin-budget/Haushaltsk_rzungen Berlin 2025.jpeg'
    }}
    metrics={[
      { value: 1, label: 'Week Prototyping' },
      { value: 38.8, label: 'Billion Euros Visualized' },
      { value: 3, label: 'Stakeholder Groups Engaged' }
    ]}
    roles={["Concept, UX, data modeling, prototyping, and visual storytelling"]}
  />

  <div class="container mx-auto px-8 mt-16">
    <ChallengeSolution
      challengeText="How might we make a complex, 2,000-page public budget draft accessible to citizens, journalists, and policymakers alike?"
      solutionText="We designed an interactive, AI-prototyped storytelling interface that visualizes Berlin's 2025 draft budget. The tool enables users to explore funding priorities, compare departments, and understand fiscal trade-offs."
    />
  </div>

  <div class="container mx-auto px-8 mt-16 mb-16">
    <h2 class="text-2xl font-light mb-4">Background</h2>
    <p>
        The Berlin Senate (CDU and SPD) has decided massive budget cuts, primarily affecting culture, transportation, environment, and urban development. A total of €3 billion is set to be saved. During our research, we found the published data to be complicated to understand and hardly accessible. That's why we took the initiative to process and present it more comprehensively. We hope our detailed breakdown of the planned cuts will increase transparency and understanding.
    <h2 class="text-2xl font-light mt-12 mb-4">Impact</h2>
    <p>
      The tool was developed in under one week using Bolt.New and Cursor, and it was used in public discourse around the budget, supporting both journalistic storytelling and citizen engagement. The work demonstrates how design, data, and AI can demystify bureaucratic systems.
    </p>
  </div>

  <p class="text-center text-gray-500 mt-4 mb-8 border-t border-b border-gray-200 pt-8 pb-8">
    Built using Bolt.New, Cursor, and custom D3 visualizations to rapidly prototype and refine the interface.
  </p>

  <div class="flex items-center justify-center mb-8">
    <div class="bg-gray-50 p-4 rounded-xl shadow flex items-center gap-4 max-w-2xl w-full">
      <img src="/projects/berlin-budget/ferat.png" alt="Ferat Koçak" class="w-80 h-80 object-cover" />
      <div>
        <p class="text-gray-700 text-sm">
          The Berlin Budget Explorer gained public traction — even Neukölln’s mayor Ferat Koçak shared it, criticizing the lack of clear communication from the administration. His post amplified the call for more transparent, citizen-friendly budgeting.
        </p>
      </div>
    </div>
  </div>

  <Quote text="Great breakdown of Berlin's budget draft! Why isn't this kind of presentation standard practice from the administration?" />

  <div class="flex flex-col items-center my-8 bg-gray-100 py-8">
    <!-- Phone frame -->
    <div class="bg-black p-2 rounded-3xl shadow-lg max-w-sm w-full">
      <video controls autoplay class="rounded-2xl w-full bg-black">
        <source src="/projects/berlin-budget/berlin-budget.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  </div>


  <Quote
    text="Making public finance legible is not just transparency—it's a democratic necessity."
  />

  <div class="max-w-6xl mx-auto px-4 py-12">
      <FullWidthImage src="/projects/berlin-budget/berlin-budget-phone.png" alt="Bildungswesen, Wissenschaft, Forschung, kulturelle Angelegenheiten" caption="Three examples of drastic cuts — from mobility to healthcare to finance — made clear and comparable at a glance." />
  </div>

  

  <ProjectNav
    prevProject={{ title: "Is This Thing On?", url: "/projects/itto" }}
    nextProject={{ title: "FactSnap", url: "/projects/fact-snap" }}
  />

  <Footer />
</div> 